import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronLeft, 
  ChevronRight, 
  Flag, 
  FlagOff, 
  SkipForward, 
  RotateCcw,
  Grid3X3,
  Bookmark,
  BookmarkCheck
} from "lucide-react";
import { cn } from "@/lib/utils";

interface FloatingNavigationBarProps {
  currentQuestion: number;
  totalQuestions: number;
  progress: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  isFlagged?: boolean;
  isBookmarked?: boolean;
  showPalette?: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onFlag?: () => void;
  onBookmark?: () => void;
  onTogglePalette?: () => void;
  onReset?: () => void;
  className?: string;
}

export const FloatingNavigationBar = ({
  currentQuestion,
  totalQuestions,
  progress,
  canGoPrevious,
  canGoNext,
  isFlagged = false,
  isBookmarked = false,
  showPalette = false,
  onPrevious,
  onNext,
  onFlag,
  onBookmark,
  onTogglePalette,
  onReset,
  className
}: FloatingNavigationBarProps) => {
  return (
    <div className={cn(
      "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50",
      "bg-card/95 backdrop-blur-sm border rounded-full shadow-lg",
      "px-4 py-3 flex items-center gap-3",
      "animate-slide-in-bottom",
      className
    )}>
      {/* Question counter */}
      <Badge variant="outline" className="px-3 py-1 font-medium">
        {currentQuestion + 1} / {totalQuestions}
      </Badge>
      
      {/* Progress bar */}
      <div className="hidden sm:flex items-center gap-2">
        <Progress value={progress} className="w-20 h-2" />
        <span className="text-xs text-muted-foreground font-medium">
          {Math.round(progress)}%
        </span>
      </div>
      
      {/* Navigation controls */}
      <div className="flex items-center gap-1">
        {/* Previous button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          className="h-8 w-8 p-0 hover:bg-primary/10"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        
        {/* Next/Skip button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onNext}
          disabled={!canGoNext}
          className="h-8 w-8 p-0 hover:bg-primary/10"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Action buttons */}
      <div className="flex items-center gap-1 border-l pl-3 ml-1">
        {/* Flag button */}
        {onFlag && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onFlag}
            className={cn(
              "h-8 w-8 p-0",
              isFlagged ? "bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/20 dark:text-orange-400" : "hover:bg-muted"
            )}
          >
            {isFlagged ? <Flag className="h-4 w-4 fill-current" /> : <FlagOff className="h-4 w-4" />}
          </Button>
        )}
        
        {/* Bookmark button */}
        {onBookmark && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onBookmark}
            className={cn(
              "h-8 w-8 p-0",
              isBookmarked ? "bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/20 dark:text-blue-400" : "hover:bg-muted"
            )}
          >
            {isBookmarked ? <BookmarkCheck className="h-4 w-4 fill-current" /> : <Bookmark className="h-4 w-4" />}
          </Button>
        )}
        
        {/* Palette toggle */}
        {onTogglePalette && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onTogglePalette}
            className={cn(
              "h-8 w-8 p-0",
              showPalette ? "bg-primary/10 text-primary" : "hover:bg-muted"
            )}
          >
            <Grid3X3 className="h-4 w-4" />
          </Button>
        )}
        
        {/* Reset button */}
        {onReset && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            className="h-8 w-8 p-0 hover:bg-muted"
          >
            <RotateCcw className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};